import { SubmissionError } from '../form';
import apiRequestActionCreator from './apiRequestActionCreator';

export function emptyCollection({ collectionActionName }) {
    return () => dispatch => dispatch({ type: `${collectionActionName}_EMPTY` });
}

export function fetchCollection({
    apiCollectionName,
    collectionActionName,
    params,
    paramsFunction,
    processSuccessResponse,
}) {
    return ({ actionParams, page, limit, requestSource, ...body } = {}) => {
        const defaultParams = { ...body, page: page - 1 || 0, size: limit || 2000 };
        return apiRequestActionCreator({
            actionName: collectionActionName,
            actionParams,
            params: {
                ...params,
                params: paramsFunction ? paramsFunction({ params: defaultParams }) : defaultParams,
                url: apiCollectionName,
            },
            processSuccessResponse,
            requestSource,
        });
    };
}

export function fetchItem({ apiCollectionName, getActionParams, itemActionName, params }) {
    return ({ id, ...queryParams } = {}) => {
        return apiRequestActionCreator({
            actionName: itemActionName,
            actionParams: getActionParams ? getActionParams({ id, ...queryParams }) : { id },
            params: {
                ...params,
                params: queryParams,
                url: `${apiCollectionName}${id ? `/${id}` : ''}`,
            },
        });
    };
}

export function processItemError({ error, method }) {
    if (method === 'delete') {
        return error;
    }

    const { data } = error?.response ?? {};
    if (data?.errors) {
        // Field errors
        throw new SubmissionError({
            error: data,
            fields: data.errors.reduce(
                (errors, err) => ({
                    ...errors,
                    [err.field]: err.defaultMessage,
                }),
                {}
            ),
        });
    }
    if (data?.message) {
        throw new Error(data.message);
    }

    return error;
}

export function updateItem({ apiCollectionName, itemActionName, params, paramsFunction }) {
    return ({ id, ...body }) =>
        apiRequestActionCreator({
            actionName: itemActionName,
            actionParams: { id },
            params: {
                ...params,
                data: paramsFunction ? paramsFunction({ params: body }) : body,
                method: 'put',
                url: `${apiCollectionName}${id ? `/${id}` : ''}`,
            },
            processError: error => processItemError({ error }),
        });
}

export function updateBulk({ apiCollectionName, itemActionName, params, paramsFunction }) {
    return ({ id, ...body }) =>
        apiRequestActionCreator({
            actionName: itemActionName,
            actionParams: { id },
            params: {
                ...params,
                data: paramsFunction ? paramsFunction({ params: body }) : body,
                method: 'put',
                url: `${apiCollectionName}/${id}/bulk`,
            },
            processError: error => processItemError({ error }),
        });
}

export function patchItem({ apiCollectionName, itemActionName, params, paramsFunction }) {
    return ({ id, ...body }) =>
        apiRequestActionCreator({
            actionName: itemActionName,
            actionParams: { id },
            params: {
                ...params,
                data: paramsFunction ? paramsFunction({ params: body }) : body,
                method: 'patch',
                url: `${apiCollectionName}/${id}`,
            },
            processError: error => processItemError({ error }),
        });
}

export function addItem({
    apiCollectionName,
    itemActionName,
    params,
    paramsFunction,
    processSuccessResponse,
}) {
    return ({ ...body }) =>
        apiRequestActionCreator({
            actionName: itemActionName,
            params: {
                ...params,
                data: paramsFunction ? paramsFunction({ params: body }) : body,
                method: 'post',
                url: apiCollectionName,
            },
            processError: error => processItemError({ error }),
            processSuccessResponse,
        });
}

export function deleteItem({ apiCollectionName, itemActionName, params, paramsFunction }) {
    return ({ id, ...body }) => {
        const method = 'delete';
        return apiRequestActionCreator({
            actionName: `DELETE_${itemActionName}`,
            actionParams: { id },
            params: {
                ...params,
                data: paramsFunction ? paramsFunction({ params: body }) : body,
                method,
                url: `${apiCollectionName}${id ? `/${id}` : ''}`,
            },
            processError: error => processItemError({ error, method }),
        });
    };
}

export function deleteItemFieldValue({
    apiCollectionName,
    itemActionName,
    params,
    paramsFunction,
}) {
    return ({ id, fieldName, ...body }) => {
        const method = 'delete';
        return apiRequestActionCreator({
            actionName: `DELETE_${itemActionName}_FIELD`,
            actionParams: { id },
            params: {
                ...params,
                data: paramsFunction ? paramsFunction({ params: body }) : body,
                method,
                url: `${apiCollectionName}/${id}/${fieldName}`,
            },
            processError: error => processItemError({ error, method }),
        });
    };
}

export function importCollection({
    apiCollectionName,
    collectionActionName,
    params,
    paramsFunction,
}) {
    const method = 'post';
    return ({ ...body } = {}) =>
        apiRequestActionCreator({
            actionName: `IMPORT_${collectionActionName}`,
            params: {
                ...params,
                data: paramsFunction ? paramsFunction({ params: body }) : body,
                method,
                url: `${apiCollectionName}/import`,
            },
        });
}

export function exportCollection({
    apiCollectionName,
    collectionActionName,
    exportFileName = 'export',
    params,
    paramsFunction,
}) {
    return ({ processSuccessFunction, ...body } = {}) =>
        apiRequestActionCreator({
            actionName: `EXPORT_${collectionActionName}`,
            params: {
                ...params,
                params: paramsFunction ? paramsFunction({ params: body }) : body,
                url: `${apiCollectionName}/export`,
            },
            processSuccessResponse: ({ data }) => {
                if (processSuccessFunction) {
                    processSuccessFunction({ data, exportFileName });
                }
                return data;
            },
        });
}
